import { useDisclosure } from '@chakra-ui/react';
import { CompanySettingsIcon } from '@monetize/ui/icons';
import { useEffect } from 'react';
import {
  ExpandableAccordion,
  MBox,
  MIcon,
  MIDCopyBox,
  MPageContainer,
  MSettingsPageHeader,
  MTag,
  MText,
  MVStack,
} from '../../../components/Monetize';
import { ButtonCard } from '../../../components/Monetize/Cards/ButtonCard';
import { ExpandCollapseButton } from '../../../components/Monetize/ExpandCollapseButton';
import { TenantForm } from '../../../components/Tenants/TenantForm';
import { SETTINGS_CONTENT_MAX_WIDTH } from '../../../constants/settings';
import { useExpandAllControl } from '../../../hooks';
import { useAuth } from '../../../services/auth0';
import { useDocumentHead } from '../../../services/documentHead';
import { AccountAndContactSettings } from './AccountAndContactSettings';
import { TenantAdditionalSettings } from './TenantAdditionalSettings';

export const TenantSettings = () => {
  const { setDocTitle } = useDocumentHead();
  const { currentTenant } = useAuth();

  useEffect(() => {
    setDocTitle('Settings', 'Tenant Settings');
  }, []);

  const {
    open: isEditTenantModalOpen,
    onOpen: onOpenEditTenantModal,
    onClose: onCloseEditTenantModal,
  } = useDisclosure();

  const {
    isAllExpanded,
    commandExpanded,
    expandCommandSeq,
    toggleExpandAll,
    onChildExpandChange,
  } = useExpandAllControl();

  return (
    <MPageContainer data-testid="quote-settings-list">
      <MSettingsPageHeader title="Tenant Settings" />

      {isEditTenantModalOpen && currentTenant && (
        <TenantForm
          isOpen
          onClose={onCloseEditTenantModal}
          existingTenant={currentTenant}
        />
      )}

      <MBox w={'100%'} mt={0} overflow="auto" mb="6" p={1}>
        <MVStack
          gap={4}
          maxW={SETTINGS_CONTENT_MAX_WIDTH}
          alignItems="flex-start"
        >
          <MBox w="100%">
            {currentTenant && (
              <ButtonCard
                minH="100px"
                title="Current Tenant"
                titleProps={{ lineClamp: 2 }}
                onClick={onOpenEditTenantModal}
                titleLeftContent={
                  <MIcon
                    as={CompanySettingsIcon}
                    w={6}
                    h={6}
                    color="tPurple.base"
                  />
                }
                titleRightContent={
                  <MTag
                    size="xs"
                    variant={currentTenant.testTenant ? 'orange' : 'purple'}
                    ml="2"
                  >
                    {currentTenant.testTenant ? 'Test' : 'Live'}
                  </MTag>
                }
                descriptionProps={{ as: 'div' }}
                description={
                  <>
                    <MText mb={1} fontSize="sm" fontWeight="500" lineClamp={2}>
                      {currentTenant.name}
                    </MText>
                    <MIDCopyBox
                      copyValue={currentTenant.id}
                      displayIcon
                      fontWeight="normal"
                    />
                  </>
                }
              />
            )}
          </MBox>

          <ExpandCollapseButton
            isExpanded={isAllExpanded}
            collapsedLabel="Expand all"
            expandedLabel="Collapse all"
            onChange={toggleExpandAll}
          />
          <ExpandableAccordion
            isAllExpanded={commandExpanded}
            expandCommandSeq={expandCommandSeq}
            onExpandChange={onChildExpandChange}
          >
            <AccountAndContactSettings />
            <TenantAdditionalSettings />
          </ExpandableAccordion>
        </MVStack>
      </MBox>
    </MPageContainer>
  );
};
